import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export const itemV = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

export const PageTransition = ({ children, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.32, ease: EASE }}>
    {children}
  </motion.div>
);

export const Stagger = ({ children, className = "", delay = 0, gap = 0.07, ...rest }) => (
  <motion.div
    className={className}
    initial="hidden"
    animate="show"
    variants={{ hidden: {}, show: { transition: { staggerChildren: gap, delayChildren: delay } } }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const Item = ({ children, className = "", ...rest }) => (
  <motion.div variants={itemV} className={className} {...rest}>
    {children}
  </motion.div>
);

export const Reveal = ({ children, className = "", delay = 0, y = 14 }) => (
  <motion.div className={className} initial={{ opacity: 0, y }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay, ease: EASE }}>
    {children}
  </motion.div>
);

export const AnimatedCard = ({ children, className = "", ...rest }) => (
  <motion.div variants={itemV} className={`surface lift ${className}`} {...rest}>
    {children}
  </motion.div>
);

export function Counter({ value = 0, suffix = "", className = "", testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, value, { duration: 0.9, ease: EASE, onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, value]);
  return (
    <span ref={ref} className={className} data-testid={testid}>
      {n}
      {suffix}
    </span>
  );
}

export function AnimatedProgress({ value = 0, className = "", height = "h-2", testid, tone = "brand" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const bg = tone === "success" || value === 100 ? "linear-gradient(90deg,#10b981,#34d399)" : "linear-gradient(90deg,#e30a17,#ff5a5f)";
  return (
    <div ref={ref} className={`w-full ${height} rounded-full bg-slate-100 overflow-hidden ${className}`} data-testid={testid} data-value={value}>
      <motion.div
        className="h-full rounded-full"
        style={{ background: bg }}
        initial={{ width: 0 }}
        animate={{ width: inView ? `${value}%` : 0 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
      />
    </div>
  );
}
